import { useEffect, useState } from 'react'
import { FilterDescriptor } from '@progress/kendo-data-query'
import { FieldSettings, TextFilter } from '@progress/kendo-react-data-tools'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchTransactionTypeAction } from 'store/transactions/transaction.action'
import { GridFilterOperatorEnum, GridFilterOperatorNameEnum } from 'types/GridFilterOperatorEnum'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { isEmpty, isNotEmpty } from 'utils/is-empty'

import {
  CompaniesFilter,
  CustomDateFilter,
  MultiSelectPaymentProviderFilter,
} from './common.filter'
import { SelectFilter } from './SelectFilter'

const TransactionStatus = ({
  filter,
  onFilterChange,
}: {
  filter: FilterDescriptor
  onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
}) => {
  const transactionStatus = useAppSelector((state) => state.transactions.status)

  const data = transactionStatus.map((x) => ({ id: x.id, label: x.name, value: x.id })) || []

  return <SelectFilter filter={filter} onFilterChange={onFilterChange} data={data} />
}

const TransactionTypeFilter = ({
  filter,
  onFilterChange,
}: {
  filter: FilterDescriptor
  onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
}) => {
  const transactionTypes = useAppSelector((state) => state.transactions.types)
  const dispatch = useAppDispatch()
  const walletsApiClient = useApiClient(WalletsApiClient)
  useEffect(() => {
    if (isEmpty(transactionTypes)) {
      dispatch(fetchTransactionTypeAction(walletsApiClient))
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const data = transactionTypes.map((x) => ({ id: x.id, label: x.name, value: x.id }))

  return (
    <SelectFilter
      filter={filter}
      onFilterChange={onFilterChange}
      data={data}
      style={{ width: 280 }}
    />
  )
}

const TradingAccountsForClientFilter = (
  clientId: string,
  {
    filter,
    onFilterChange,
  }: { filter: FilterDescriptor; onFilterChange: (event: { nextFilter: FilterDescriptor }) => void }
) => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const [tradingAccounts, setTradingAccounts] = useState<{ id: string; name: string }[]>([])

  useEffect(() => {
    if (isEmpty(tradingAccounts)) {
      ;(async () => {
        try {
          const response =
            await tradingAccountsApiClient.getWalletWithTradingAccountByClientId(clientId)
          const tradingAccountsData = response
            .filter((x) => isNotEmpty(x.tradingAccounts))
            .map((x) => x.tradingAccounts.map((wta) => ({ id: wta.id, name: wta.name })))
            .reduce((x, y) => x.concat(y), [])

          setTradingAccounts(tradingAccountsData)
        } catch (error) {
          console.info('Cannot get trading accounts list', error)
        }
      })()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const data = tradingAccounts.map((x) => ({ id: x.id, label: x.name, value: x.id })) || []

  return <SelectFilter filter={filter} onFilterChange={onFilterChange} data={data} />
}

const WalletForClientFilter = (
  clientId: string,
  {
    filter,
    onFilterChange,
  }: { filter: FilterDescriptor; onFilterChange: (event: { nextFilter: FilterDescriptor }) => void }
) => {
  const [walletsOption, setWalletOption] = useState<{ id: string; name: string }[]>([])
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  useEffect(() => {
    if (isEmpty(walletsOption)) {
      ;(async () => {
        try {
          const response =
            await tradingAccountsApiClient.getWalletWithTradingAccountByClientId(clientId)
          setWalletOption(response.map((x) => ({ id: x.id, name: x.name })))
        } catch (error) {
          console.info('Cannot get trading accounts list', error)
        }
      })()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const data = walletsOption.map((x) => ({ id: x.id, label: x.name, value: x.id })) || []

  return <SelectFilter filter={filter} onFilterChange={onFilterChange} data={data} />
}

const getTransactionOverviewForWalletOrClientFields = (
  clientId: string,
  walletId?: string
): FieldSettings[] => {
  const walletFieldSettings: FieldSettings[] = !walletId
    ? [
        {
          name: 'Wallet',
          label: 'Wallet',
          filter: (props: {
            filter: FilterDescriptor
            onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
          }) => WalletForClientFilter(clientId, props),
          operators: [
            {
              text: GridFilterOperatorNameEnum.equalOperator,
              operator: GridFilterOperatorEnum.equalOperator,
            },
            {
              text: GridFilterOperatorNameEnum.notEqOperator,
              operator: GridFilterOperatorEnum.notEqOperator,
            },
          ],
        },
      ]
    : []

  return [
    ...walletFieldSettings,
    {
      name: 'TradingAccount',
      label: 'Trading Account',
      filter: (props: {
        filter: FilterDescriptor
        onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
      }) => TradingAccountsForClientFilter(clientId, props),
      operators: [
        {
          text: GridFilterOperatorNameEnum.equalOperator,
          operator: GridFilterOperatorEnum.equalOperator,
        },
        {
          text: GridFilterOperatorNameEnum.notEqOperator,
          operator: GridFilterOperatorEnum.notEqOperator,
        },
      ],
    },
    {
      name: 'PaymentProvider',
      label: 'Payment Provider',
      filter: MultiSelectPaymentProviderFilter,
      operators: [
        {
          text: GridFilterOperatorNameEnum.equalOperator,
          operator: GridFilterOperatorEnum.equalOperator,
        },
        {
          text: GridFilterOperatorNameEnum.notEqOperator,
          operator: GridFilterOperatorEnum.notEqOperator,
        },
      ],
    },
    {
      name: 'Amount',
      label: 'Amount',
      filter: TextFilter,
      operators: [
        {
          text: GridFilterOperatorNameEnum.equalOperator,
          operator: GridFilterOperatorEnum.equalOperator,
        },
        {
          text: GridFilterOperatorNameEnum.notEqOperator,
          operator: GridFilterOperatorEnum.notEqOperator,
        },
        {
          text: GridFilterOperatorNameEnum.gtOperator,
          operator: GridFilterOperatorEnum.afterOperator,
        },
        {
          text: GridFilterOperatorNameEnum.gteOperator,
          operator: GridFilterOperatorEnum.afterOrEqualOperator,
        },
        {
          text: GridFilterOperatorNameEnum.ltOperator,
          operator: GridFilterOperatorEnum.beforeOperator,
        },
        {
          text: GridFilterOperatorNameEnum.lteOperator,
          operator: GridFilterOperatorEnum.beforeOperator,
        },
      ],
    },
    {
      name: 'Reference',
      label: 'Reference',
      filter: TextFilter,
      operators: [
        {
          text: GridFilterOperatorNameEnum.equalOperator,
          operator: GridFilterOperatorEnum.equalOperator,
        },
        {
          text: GridFilterOperatorNameEnum.notEqOperator,
          operator: GridFilterOperatorEnum.notEqOperator,
        },
      ],
    },
    {
      name: 'Type',
      label: 'Type',
      filter: TransactionTypeFilter,
      operators: [
        {
          text: GridFilterOperatorNameEnum.equalOperator,
          operator: GridFilterOperatorEnum.equalOperator,
        },
        {
          text: GridFilterOperatorNameEnum.notEqOperator,
          operator: GridFilterOperatorEnum.notEqOperator,
        },
      ],
    },
    {
      name: 'State',
      label: 'State',
      filter: TransactionStatus,
      operators: [
        {
          text: GridFilterOperatorNameEnum.equalOperator,
          operator: GridFilterOperatorEnum.equalOperator,
        },
        {
          text: GridFilterOperatorNameEnum.notEqOperator,
          operator: GridFilterOperatorEnum.notEqOperator,
        },
      ],
    },
    {
      name: 'TickmillCompany',
      label: 'Company',
      filter: CompaniesFilter,
      operators: [
        {
          text: GridFilterOperatorNameEnum.equalOperator,
          operator: GridFilterOperatorEnum.equalOperator,
        },
        {
          text: GridFilterOperatorNameEnum.notEqOperator,
          operator: GridFilterOperatorEnum.notEqOperator,
        },
      ],
    },
    {
      name: 'DateCreated',
      label: 'Date Created',
      filter: CustomDateFilter,
      operators: [
        {
          text: GridFilterOperatorNameEnum.equalOperator,
          operator: GridFilterOperatorEnum.equalOperator,
        },
        {
          text: GridFilterOperatorNameEnum.notEqOperator,
          operator: GridFilterOperatorEnum.notEqOperator,
        },
        {
          text: GridFilterOperatorNameEnum.afterOperator,
          operator: GridFilterOperatorEnum.afterOperator,
        },
        {
          text: GridFilterOperatorNameEnum.afterOrEqualOperator,
          operator: GridFilterOperatorEnum.afterOrEqualOperator,
        },
        {
          text: GridFilterOperatorNameEnum.beforeOrEqualOperator,
          operator: GridFilterOperatorEnum.beforeOrEqualOperator,
        },
        {
          text: GridFilterOperatorNameEnum.beforeOperator,
          operator: GridFilterOperatorEnum.beforeOperator,
        },
      ],
    },
  ]
}

export { getTransactionOverviewForWalletOrClientFields }
